<script setup lang="ts">
import SearchField from '@/components/SearchField.vue'
import ConfirmDlg from '@/components/confirm-dlg.vue'
import { errToStr } from '@/helpers/tools'
import { Adresse, AdresseCollection } from '@/models/Adresse'
import { useToast } from '@/composables/toast'
import { computed, type ComputedRef, type Ref, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { DataTableHeader, ItemSlotBase } from 'vuetify/lib/components/VDataTable/types.mjs'

type HeaderWidthType = {
    typ: string
    name: string
    vorname: string
    adresse: string
    plz: string
    ort: string
    land: string
    action: string
}

const {
    addbtn,
    edit = true,
    dense = false,
    nofooter = false,
    selectable = false,
    height,
    selection = null,
    reload = false,
    headerwidths = {
        typ: '55px',
        name: '15%',
        vorname: '15%',
        adresse: '30%',
        plz: '10%',
        ort: '20%',
        action: '110px',
    },
    gesuchId = null,
    typfilter = null,
} = defineProps<{
    addbtn?: string
    edit?: boolean
    dense?: boolean
    nofooter?: boolean
    selectable?: boolean
    height?: number
    selection?: Adresse | Adresse[] | null
    headerwidths?: HeaderWidthType
    gesuchId?: number | null
    typfilter?: string | null
    reload?: boolean
}>()

const adressen = new AdresseCollection()
const adrLoading = ref(false)
const calculateWidth = ref(false)
const searchString = ref('')
const total = ref(0)
const selectedItems: Ref<Adresse[]> = ref([])
const confirmDelDlg = ref(false)
const toDelete: Ref<Adresse | null> = ref(null)
const options = ref({
    page: 1,
    itemsPerPage: 50,
    sortBy: ['display_name'],
    sortDesc: [false],
    multiSort: false,
    mustSort: true,
})

const { t } = useI18n()

const headers: ComputedRef<DataTableHeader[]> = computed(() => {
    const res: DataTableHeader[] = [
        { value: 'typ', sortable: false, width: headerwidths.typ },
        {
            title: headerwidths.name !== '0%' ? t('name') : '',
            key: 'display_name',
            sortable: true,
            width: headerwidths.name,
        },
        {
            title: headerwidths.vorname !== '0%' ? t('vorname') : '',
            key: 'vorname',
            sortable: true,
            width: headerwidths.vorname,
        },
        {
            title: headerwidths.adresse !== '0%' ? t('adresse') : '',
            key: 'adresse',
            sortable: true,
            width: headerwidths.adresse,
        },
        {
            title: headerwidths.plz !== '0%' ? t('plz') : '',
            key: 'plz',
            sortable: true,
            width: headerwidths.plz,
        },
        {
            title: headerwidths.ort !== '0%' ? t('ort') : '',
            key: '$.ort',
            sortable: true,
            width: headerwidths.ort,
        },
    ]
    if (edit) {
        res.push({
            title: '',
            key: 'action',
            sortable: false,
            width: headerwidths.action,
            minWidth: headerwidths.action,
        })
    }
    return res
})

const emit = defineEmits<{
    (e: 'reload'): void
    (e: 'new-address'): void
    (e: 'selection', item: Adresse | null): void
}>()

async function getAdressen({
    page,
    itemsPerPage,
    sortBy,
}: {
    page: number
    itemsPerPage: number
    sortBy: string[]
}) {
    adrLoading.value = true
    try {
        if (typfilter) {
            adressen.setQueryParam('typ', typfilter)
        }
        adressen.setQueryParam('gesuchId', gesuchId)
        adressen.setQueryParam('itemsPerPage', itemsPerPage)
        adressen.setQueryParam('page', page)
        adressen.setQueryParam('sortBy', sortBy)
        adressen.setQueryParam('query', searchString.value)
        await adressen.query()
        total.value = adressen.getServerTotal()
    } catch (e) {
        console.log(e)
    } finally {
        adrLoading.value = false
    }
}
function executeSearch(value: string) {
    searchString.value = value
}
const router = useRouter()

function showForm(item: Adresse) {
    router.push({ name: 'adresse', params: { id: item.id || 'new' } })
}

function onRowClicked(event: PointerEvent, row: ItemSlotBase<Adresse>) {
    if (selectable) {
        selectedItems.value = [row.item]
    } else {
        showForm(row.item)
    }
}

watch(selectedItems, () => {
    emitSelection()
})

watch(
    () => selection,
    (val: Adresse | Adresse[] | null) => {
        if (val) {
            if (!Array.isArray(val)) {
                val = [val]
            }
            selectedItems.value = val
        }
    },
)

watch(
    () => reload,
    (val) => {
        if (val) {
            getAdressen(options.value)
            emit('reload')
        }
    },
)

function emitSelection() {
    if (selectedItems.value.length === 1) {
        emit('selection', selectedItems.value[0])
    } else {
        emit('selection', null)
    }
}
function destroyItem(item: Adresse) {
    toDelete.value = item
    confirmDelDlg.value = true
}
async function confirmDestroy() {
    try {
        await toDelete.value?.destroy()
        if (toDelete.value) {
            adressen.remove(toDelete.value)
        }
        toDelete.value = null
        total.value = total.value - 1
        confirmDelDlg.value = false
    } catch (e) {
        useToast().add({ text: errToStr(e), color: 'error' })
    }
}
</script>

<template>
    <v-data-table-server
        v-model:model-value="selectedItems"
        :class="{
            'adresse-liste': true,
            'fixed-table': true,
            'fixed-overflow-table': !dense,
            'row-pointer': true,
        }"
        :headers="headers"
        :calculate-widths="calculateWidth"
        :items="adressen.getModels()"
        v-model:options="options"
        :items-length="total"
        :items-per-page="50"
        :loading="adrLoading"
        :dense="dense"
        :search="searchString"
        select-strategy="single"
        item-key="id"
        :return-object="true"
        :show-select="selectable"
        :hide-default-footer="nofooter"
        :no-data-text="$t('no_records')"
        :height="height"
        :footer-props="{
            itemsPerPageOptions: [10, 25, 50, 100, -1],
        }"
        @click:row="onRowClicked"
        @update:options="getAdressen"
    >
        <template v-slot:top>
            <div class="d-flex align-end justify-end">
                <SearchField
                    class="white--text"
                    @executeSearch="executeSearch"
                    max-width="500px"
                    :width="addbtn ? '65vw' : '90vw'"
                ></SearchField>
                <v-btn
                    v-if="addbtn"
                    variant="flat"
                    color="action white--text"
                    class="hidden-sm-and-up add-button"
                    justify-center
                    rounded="xl"
                    icon="fas fa-plus"
                    size="small"
                    @click="$emit('new-address')"
                >
                </v-btn>
                <v-btn
                    v-if="addbtn"
                    variant="flat"
                    color="action white--text"
                    class="hidden-xs add-button"
                    justify-center
                    rounded="xl"
                    prepend-icon="fas fa-plus fa-xs"
                    @click="$emit('new-address')"
                >
                    {{ addbtn }}
                </v-btn>
            </div>
        </template>

        <template v-if="dense" v-slot:[`item.typ`]="{ item }">
            <v-tooltip right>
                <template v-slot:activator="{ props }">
                    <v-icon v-if="item.typ === 'person'" size="x-small" v-bind="props"
                        >fas fa-user</v-icon
                    >
                </template>
                <span>{{ $t('person') }}</span>
            </v-tooltip>
            <v-tooltip right>
                <template v-slot:activator="{ props }">
                    <v-icon v-if="item.typ === 'organisation'" size="x-small" v-bind="props">
                        fas fa-building
                    </v-icon>
                </template>
                <span>{{ $t('organisation') }}</span>
            </v-tooltip>
        </template>
        <template v-else v-slot:[`item.typ`]="{ item }">
            <v-tooltip right>
                <template v-slot:activator="{ props }">
                    <v-icon v-if="item.typ === 'person'" v-bind="props">fas fa-user</v-icon>
                </template>
                <span>{{ $t('person') }}</span>
            </v-tooltip>
            <v-tooltip right>
                <template v-slot:activator="{ props }">
                    <v-icon v-if="item.typ === 'organisation'" v-bind="props">
                        fas fa-building
                    </v-icon>
                </template>
                <span>{{ $t('organisation') }}</span>
            </v-tooltip>
        </template>
        <template v-slot:[`item.action`]="{ item }">
            <v-btn
                class="mr-1"
                size="x-small"
                variant="text"
                color="accent"
                icon="fas fa-pen"
                @click.stop="showForm(item)"
            >
            </v-btn>
            <v-btn
                class="mr-1"
                size="x-small"
                variant="text"
                color="error"
                icon="fas fa-trash"
                @click.stop="destroyItem(item)"
            >
            </v-btn>
        </template>
        <template v-slot:[`item.display_name`]="{ item }">
            {{ item.display_name }}<br />{{ item.nachname2 }}
        </template>
        <template v-slot:[`item.vorname`]="{ item }"
            >{{ item.vorname }}<br />{{ item.vorname2 }}</template
        >
        <template v-slot:[`item.plz`]="{ item }">
            <template v-if="item.laendercode === 'CH'">{{ item.plz }}</template>
            <template v-else
                >{{ item.laendercode ? item.laendercode + '-' : '' }}{{ item.plz }}</template
            >
        </template>
    </v-data-table-server>
    <ConfirmDlg
        v-model:model-value="confirmDelDlg"
        :title="$t('bestaetigung')"
        :text="$t('really_delete_rec')"
        @yes="confirmDestroy"
        @no="confirmDelDlg = false"
    />
</template>

<style scoped>
.fixed-table {
    :deep(table) {
        table-layout: fixed;
        width: 100%;
    }
}
.row-pointer {
    :deep(tbody tr :hover) {
        cursor: pointer;
    }
}
@media screen and (min-width: 600px) {
    .fixed-table {
        :deep(table td) {
            text-overflow: ellipsis;
        }
    }
    .fixed-overflow-table {
        :deep(table td) {
            overflow: hidden;
        }
    }
}
@media screen and (max-width: 600px) {
    .fixed-table {
        :deep(table th) {
            width: 100%;
        }
    }
}

.search-container {
    display: flex;
    margin: 1rem 0 1rem 1rem;
}
.search-box {
    flex: 1;
}
.add-button {
    margin-left: 1rem;
    align-self: center;
}

.adresse-liste {
    :deep(tbody tr:hover) {
        background-color: #f7f7f7;
    }
}
</style>
